import { apiClient } from "@/api/config/api-client";
import { API_ENDPOINTS } from "@/api/config/api-endpoints";
import * as Sentry from "@sentry/react";
import mixpanel from "mixpanel-browser";
import { track } from "@/lib/analytics";
import { reRegisterExposures } from "@/lib/growthbook";
import { registerClientSource } from "@/lib/client-source";
import qs from "qs";
import {
  consumeAuthGateRedirect,
  readLastVisitedRoute,
} from "@/features/auth-gate";

// Backend'den dönen response tipi
export interface UserResponse {
  success: boolean;
  message: string;
  data: {
    _id: string;
    name: string;
    email: string;
    image: string;
    isVerified: boolean;
    provider: string;
    role: string;
    theme: string;
    token: number;
  };
}

export interface EstablishSessionArgs {
  accessToken: string;
  refreshToken: string | null;
  isNewUser: boolean;
  provider: string;
  setTokens: (tokens: {
    accessToken: string;
    refreshToken?: string | null;
  }) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setUser: (user: any) => void;
  success: (message: string, options?: { title?: string }) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
  navigate: (path: string) => void;
}

/**
 * Token'lardan oturum kurar: profili çeker, analytics kimliklerini bağlar ve
 * kullanıcıyı yönlendirir.
 *
 * Hem redirect akışı (OAuthCallbackPage) hem de Apple popup akışı bunu kullanır.
 * Popup akışı token'ları zaten elinde tuttuğu için sayfa yönlendirmesi olmadan
 * doğrudan buraya girer.
 */
export async function establishSessionFromTokens({
  accessToken,
  refreshToken,
  isNewUser,
  provider,
  setTokens,
  setUser,
  success,
  t,
  navigate,
}: EstablishSessionArgs): Promise<void> {
  setTokens({ accessToken, refreshToken });

  let userData: UserResponse;
  try {
    userData = await apiClient.get<UserResponse>(API_ENDPOINTS.getMe);
  } catch (userError) {
    // Profil alınamadıysa yarım oturumla devam etmeyelim.
    setTokens({ accessToken: "", refreshToken: null });
    throw userError;
  }

  if (!userData?.data) {
    setTokens({ accessToken: "", refreshToken: null });
    throw new Error("Kullanıcı bilgileri alınamadı");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setUser(userData.data as any);
  Sentry.setUser({
    id: userData.data._id,
    email: userData.data.email,
  });

  try {
    if (isNewUser) {
      mixpanel.alias(userData.data._id);
    } else {
      mixpanel.identify(userData.data._id);
    }
    mixpanel.people.set({
      $name: userData.data.name,
      $email: userData.data.email,
      ...(isNewUser && { $created: new Date().toISOString() }),
    });
    // GA4 karşılıkları `sign_up` / `login`. dataLayer'a yalnızca kişisel veri
    // içermeyen alanlar geçiyor.
    track(
      isNewUser ? "user_registered" : "user_logged_in",
      {
        type: isNewUser ? `${provider}_register` : `${provider}_login`,
        email: userData.data.email,
        name: userData.data.name,
        _id: userData.data._id,
      },
      {
        dataLayerEvent: isNewUser ? "sign_up" : "login",
        dataLayerProps: {
          method: provider,
          user_id: userData.data._id,
        },
      },
    );

    // Logout `mixpanel.reset()` çağırıyor (Profile.tsx) ve cookie ile birlikte
    // deney varyantlarını taşıyan super property'ler de siliniyor. Geri
    // yazılmazsa çıkıp tekrar giren kullanıcı funnel kırılımından sessizce
    // düşer — hiçbir yerde hata görünmez, sadece sayılar eksik olur.
    reRegisterExposures();
    // Aynı gerekçe `source` için de geçerli: o da bir super property ve
    // `reset()` onu da siliyor.
    registerClientSource();
  } catch (e) {
    console.warn("mx error", e);
  }

  window.netmera.push((api) => {
    const user = api.getUser();
    user.setName(userData.data.name);
    user.setEmail(userData.data.email);
    user.setCustomId(userData.data._id);
    user.save();

    api.sendEvent({
      code: "n:cl",
      uid: userData.data._id,
    });
  });

  success(
    t("auth.welcomeBack", {
      name: userData.data.name || userData.data.email,
    }),
    {
      title: t("auth.loginSuccess"),
    },
  );

  // Auth gate popup'ından gelen kullanıcıyı kaldığı sayfaya geri götür
  const authGateRedirect = consumeAuthGateRedirect();
  if (authGateRedirect) {
    navigate(authGateRedirect);
    return;
  }

  // Login öncesi gezilen son sayfaya dön; yeni kullanıcı ana sayfada
  // karşılanmalı (onboarding oradan tetikleniyor)
  const lastRoute = readLastVisitedRoute();
  if (!isNewUser && lastRoute) {
    navigate(lastRoute);
    return;
  }

  navigate(isNewUser ? "/" : `/?${qs.stringify({ returning_user: 1 })}`);
}

/**
 * Netmera "login failed" event'i (code: etoic).
 *
 * DİKKAT: parametre sırası (loginMethod, failureReason) ama payload'da
 * ea = failureReason, eb = loginMethod. LoginPage'deki orijinal davranışla
 * birebir aynı — karıştırmayın.
 */
export const NetmeraLoginFailedEvent = (
  loginMethod: string,
  failureReason: string,
) => {
  window.netmera.push(function (api) {
    api.sendEvent({
      code: "etoic",
      ea: failureReason,
      eb: loginMethod,
    });
  });
};
